/**
 * Shared types for floimg-studio
 */
export { mapValidationToNodes, formatValidationResponse, validateInputFormats, } from "./validation.js";
/**
 * Build dependency graph from nodes and edges
 */
function buildDependencyGraph(nodes, edges) {
    const dependencies = new Map();
    for (const node of nodes) {
        dependencies.set(node.id, new Set());
    }
    for (const edge of edges) {
        dependencies.get(edge.target)?.add(edge.source);
    }
    return dependencies;
}
/**
 * Convert visual editor workflow (nodes + edges) to Pipeline format
 *
 * This is a pure conversion function - no API key injection or runtime concerns.
 * The resulting Pipeline can be sent to the backend for execution.
 *
 * Variable names (the `out` field) serve as step identifiers throughout
 * the system - they're used in SSE events, data flow, and results.
 *
 * @param nodes - Workflow nodes from the visual editor
 * @param edges - Connections between nodes
 * @returns Pipeline and node-to-variable mapping
 */
export function nodesToPipeline(nodes, edges) {
    // Build a map of fan-out node IDs to their output counts
    const fanoutNodes = new Map();
    for (const node of nodes) {
        if (node.type === "fanout") {
            const data = node.data;
            fanoutNodes.set(node.id, {
                count: data.count || 3,
                mode: data.mode || "count",
            });
        }
    }
    // Topological sort for execution order
    const dependencies = buildDependencyGraph(nodes, edges);
    const completed = new Set();
    const sorted = [];
    while (sorted.length < nodes.length) {
        const ready = nodes.filter((n) => {
            if (completed.has(n.id))
                return false;
            const deps = dependencies.get(n.id) || new Set();
            for (const dep of deps) {
                if (!completed.has(dep))
                    return false;
            }
            return true;
        });
        if (ready.length === 0)
            break;
        for (const node of ready) {
            sorted.push(node);
            completed.add(node.id);
        }
    }
    const nodeToVar = new Map();
    const steps = [];
    const inputNodeVars = [];
    for (let i = 0; i < sorted.length; i++) {
        const node = sorted[i];
        // Use node ID as variable name - ensures consistency between visual editor and Pipeline
        const varName = node.id;
        nodeToVar.set(node.id, varName);
        // Input nodes don't create pipeline steps - data injected via initialVariables
        // Track them so we can add to initialVariables for validation
        if (node.type === "input") {
            inputNodeVars.push(varName);
            continue;
        }
        if (node.type === "generator") {
            const data = node.data;
            const params = { ...data.params };
            // Find text input edge (for AI generators with dynamic prompts)
            const textEdge = edges.find((e) => e.target === node.id && e.targetHandle === "text");
            const textSourceVar = textEdge ? nodeToVar.get(textEdge.source) : undefined;
            const textSourceHandle = textEdge?.sourceHandle;
            // Check if text input comes from a fan-out branch
            const fanoutBranchMatch = textSourceHandle?.match(/^out\[(\d+)\]$/);
            const isFanoutBranch = fanoutBranchMatch && textEdge && fanoutNodes.has(textEdge.source);
            if (isFanoutBranch && textEdge) {
                const branchIndex = parseInt(fanoutBranchMatch[1], 10);
                const fanoutVar = nodeToVar.get(textEdge.source);
                params._promptFromVar = `${fanoutVar}_${branchIndex}`;
            }
            else if (textSourceVar) {
                params._promptFromVar = textSourceVar;
                if (textSourceHandle?.startsWith("output.")) {
                    params._promptFromProperty = textSourceHandle.slice(7);
                }
            }
            // Find reference image edges
            const referenceEdges = edges.filter((e) => e.target === node.id && e.targetHandle === "references");
            if (referenceEdges.length > 0) {
                const refVars = referenceEdges
                    .map((e) => nodeToVar.get(e.source))
                    .filter((v) => v !== undefined);
                if (refVars.length > 0) {
                    params._referenceImageVars = refVars;
                }
            }
            steps.push({
                kind: "generate",
                generator: data.generatorName,
                params,
                out: varName,
            });
        }
        else if (node.type === "transform") {
            const data = node.data;
            const isComposite = data.operation === "composite";
            // For composite, accept "base" handle; otherwise "image" or undefined
            const imageEdge = edges.find((e) => {
                if (e.target !== node.id)
                    return false;
                if (isComposite) {
                    return e.targetHandle === "base" || e.targetHandle === "image" || !e.targetHandle;
                }
                return e.targetHandle === "image" || !e.targetHandle;
            });
            const inputVar = imageEdge ? nodeToVar.get(imageEdge.source) : undefined;
            if (!inputVar) {
                throw new Error(`Transform node requires an input image connection`);
            }
            const textEdge = edges.find((e) => e.target === node.id && e.targetHandle === "text");
            const textSourceVar = textEdge ? nodeToVar.get(textEdge.source) : undefined;
            const textSourceHandle = textEdge?.sourceHandle;
            const params = { ...data.params };
            if (textSourceVar) {
                params._promptFromVar = textSourceVar;
                if (textSourceHandle?.startsWith("output.")) {
                    params._promptFromProperty = textSourceHandle.slice(7);
                }
            }
            const referenceEdges = edges.filter((e) => e.target === node.id && e.targetHandle === "references");
            if (referenceEdges.length > 0) {
                const refVars = referenceEdges
                    .map((e) => nodeToVar.get(e.source))
                    .filter((v) => v !== undefined);
                if (refVars.length > 0) {
                    params._referenceImageVars = refVars;
                }
            }
            // Handle composite overlay inputs - collect edges with "overlays[N]" handles
            // Store both variable name and original index to support sparse arrays
            if (isComposite) {
                const overlayEdges = edges
                    .filter((e) => e.target === node.id && e.targetHandle?.startsWith("overlays["))
                    .sort((a, b) => {
                    // Sort by index to maintain order
                    const idxA = parseInt(a.targetHandle?.match(/\[(\d+)\]/)?.[1] || "0", 10);
                    const idxB = parseInt(b.targetHandle?.match(/\[(\d+)\]/)?.[1] || "0", 10);
                    return idxA - idxB;
                });
                if (overlayEdges.length > 0) {
                    const overlayVarsWithIndex = overlayEdges
                        .map((e) => {
                        const varName = nodeToVar.get(e.source);
                        const index = parseInt(e.targetHandle?.match(/\[(\d+)\]/)?.[1] || "0", 10);
                        return varName ? { varName, index } : null;
                    })
                        .filter((v) => v !== null);
                    if (overlayVarsWithIndex.length > 0) {
                        params._overlayImageVars = overlayVarsWithIndex;
                    }
                }
            }
            steps.push({
                kind: "transform",
                op: data.operation,
                in: inputVar,
                params,
                out: varName,
                ...(data.providerName && { provider: data.providerName }),
            });
        }
        else if (node.type === "save") {
            const data = node.data;
            const inputEdge = edges.find((e) => e.target === node.id);
            const inputVar = inputEdge ? nodeToVar.get(inputEdge.source) : undefined;
            if (!inputVar) {
                throw new Error(`Save node requires an input connection`);
            }
            steps.push({
                kind: "save",
                in: inputVar,
                destination: data.destination,
                provider: data.provider,
            });
        }
        else if (node.type === "vision") {
            const data = node.data;
            const inputEdge = edges.find((e) => e.target === node.id && (e.targetHandle === "image" || !e.targetHandle));
            const inputVar = inputEdge ? nodeToVar.get(inputEdge.source) : undefined;
            if (!inputVar) {
                throw new Error(`Vision node requires an input image connection`);
            }
            const contextEdge = edges.find((e) => e.target === node.id && e.targetHandle === "context");
            const contextSourceVar = contextEdge ? nodeToVar.get(contextEdge.source) : undefined;
            const params = { ...data.params };
            if (contextSourceVar) {
                params._contextFromVar = contextSourceVar;
            }
            steps.push({
                kind: "vision",
                provider: data.providerName,
                in: inputVar,
                params,
                out: varName,
            });
        }
        else if (node.type === "text") {
            const data = node.data;
            const inputEdge = edges.find((e) => e.target === node.id);
            const inputVar = inputEdge ? nodeToVar.get(inputEdge.source) : undefined;
            steps.push({
                kind: "text",
                provider: data.providerName,
                in: inputVar,
                params: { ...data.params },
                out: varName,
            });
        }
        else if (node.type === "fanout") {
            const data = node.data;
            const inputEdge = edges.find((e) => e.target === node.id);
            const inputVar = inputEdge ? nodeToVar.get(inputEdge.source) : undefined;
            const branchCount = data.count || 3;
            const branchVars = Array.from({ length: branchCount }, (_, i) => `${varName}_${i}`);
            steps.push({
                kind: "fan-out",
                in: inputVar || "",
                mode: data.mode || "count",
                count: branchCount,
                arrayProperty: data.arrayProperty,
                out: branchVars,
            });
        }
        else if (node.type === "collect") {
            const data = node.data;
            const inputEdges = edges.filter((e) => e.target === node.id);
            const inputVars = inputEdges
                .map((e) => nodeToVar.get(e.source))
                .filter((v) => v !== undefined);
            const waitMode = data.waitMode || "all";
            steps.push({
                kind: "collect",
                in: inputVars,
                waitMode,
                ...(waitMode === "available" && { minRequired: Math.max(1, data.expectedInputs || 1) }),
                out: varName,
            });
        }
        else if (node.type === "router") {
            const data = node.data;
            const candidatesEdge = edges.find((e) => e.target === node.id && e.targetHandle === "candidates");
            const selectionEdge = edges.find((e) => e.target === node.id && e.targetHandle === "selection");
            const candidatesVar = candidatesEdge ? nodeToVar.get(candidatesEdge.source) : undefined;
            const selectionVar = selectionEdge ? nodeToVar.get(selectionEdge.source) : undefined;
            const selectionType = data.selectionType === "value" ? "property" : data.selectionType || "index";
            steps.push({
                kind: "router",
                in: candidatesVar || "",
                selectionIn: selectionVar || "",
                selectionType: selectionType,
                selectionProperty: data.selectionProperty || "winner",
                out: varName,
            });
        }
    }
    // Build initialVariables for input nodes (placeholder values for validation)
    // Actual data is injected at runtime via initialVariablesBase64
    const initialVariables = inputNodeVars.length > 0 ? Object.fromEntries(inputNodeVars.map((v) => [v, null])) : undefined;
    return {
        pipeline: {
            name: "Studio Workflow",
            steps,
            ...(initialVariables && { initialVariables }),
        },
        nodeToVar,
    };
}
//# sourceMappingURL=index.js.map